import { useEffect, useState } from "react";
import { useLogoStore } from "../state/logoStore";
import { AccountFlyout } from "../components/AccountFlyout";
import { PageLabel } from "../components/ui/PageLabel";
import { renderLogoSVG } from "../engine/render";
import { useAuth } from "../hooks/useAuth";
import { fetchRecentPublicLogos, type PublicLogoRow } from "../lib/publicLogos";

// === PAGE: HOME (StartPage) ===
export function StartPage() {
  const setPage = useLogoStore((s) => s.setPage);
  const [accountOpen, setAccountOpen] = useState(false);

  function go(target: "templates" | "editor" | "onboarding" | "generated" | "gif-maker" | "qr-maker") {
    setPage(target);
    window.scrollTo({ top: 0 });
  }

  return (
    <div className="page home">
      <PageLabel name="HOME" />
      <TopNav onSignIn={() => setAccountOpen(true)} />
      <AccountFlyout open={accountOpen} onClose={() => setAccountOpen(false)} />

      {/* ============ HERO ============ */}
      <section className="hero hero-v2">
        <ConfettiDecor />
        <div className="hero-grid">
          <div className="hero-copy">
            <h1 className="hero-title">
              Design Your <span className="ink-coral">Logo.</span>
              <br />
              Build Your <span className="ink-pink">Brand.</span>
            </h1>
            <p className="hero-lede">
              Create stunning logos, animated GIFs, and branded materials in minutes.
              Affordable, powerful and made for entrepreneurs like you.
            </p>

            <div className="hero-cta-row">
              <button className="btn-warm-primary" onClick={() => go("onboarding")}>
                Start Designing Free
              </button>
              <button className="btn-warm-ghost" onClick={() => go("templates")}>
                <span aria-hidden="true" style={{ marginRight: 8 }}>⊞</span> Explore Templates
              </button>
            </div>

            <div className="trust-bar">
              <div className="trust-item">
                <span className="trust-icon trust-icon-no">✕</span>
                <div>
                  <div className="trust-title">No Ads</div>
                  <div className="trust-sub">100% Ad-Free</div>
                </div>
              </div>
              <div className="trust-item">
                <span className="trust-icon trust-icon-low">↓</span>
                <div>
                  <div className="trust-title">Low Cost</div>
                  <div className="trust-sub">High Value</div>
                </div>
              </div>
              <div className="trust-item">
                <span className="trust-icon trust-icon-free">⊞</span>
                <div>
                  <div className="trust-title">Free to Try</div>
                  <div className="trust-sub">Experiment Freely</div>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-art">
            <RecentLogosCarousel />
          </div>
        </div>
      </section>

      {/* ============ FEATURE GRID ============ */}
      <FeatureGrid go={go} />

      {/* ============ MANIFESTO POSTER ============ */}
      <ManifestoSection />

      {/* ============ HOW IT WORKS ============ */}
      <section className="section-wrap">
        <div className="section-head-center">
          <h2>How it works</h2>
          <p>From blank canvas to brand kit in five steps.</p>
        </div>

        <Step
          num="01"
          title="Start with design inspiration"
          body="Enter your brand name and (optionally) drop in a reference image — your office photo, a screenshot of a website you like, a fabric swatch, anything. LOGOMAN extracts the dominant colors and uses them across every template and variation it generates for you."
          ctaLabel="Get started"
          onCta={() => go("onboarding")}
          art={<HowArt step={1} />}
          flip={false}
        />

        <Step
          num="02"
          title="Browse logos that already match your colors"
          body="The template gallery generates designs using your extracted palette first — so every logo on the page already feels like your brand. Click any one to drop into the editor."
          ctaLabel="See templates"
          onCta={() => go("onboarding")}
          art={<HowArt step={2} />}
          flip={true}
        />

        <Step
          num="03"
          title="Perfect your design"
          body="Tweak the icon (20+ shapes across 5 categories), title font, tagline font, sizes, layout, and every color individually. The live SVG preview updates as you edit. Pick from extracted palette swatches or pull custom hex codes from the native color picker."
          ctaLabel="Open editor"
          onCta={() => go("editor")}
          art={<HowArt step={3} />}
          flip={false}
        />

        <Step
          num="04"
          title="Save your draft as you work"
          body="Every change auto-saves to your browser. Close the tab, restart your laptop, come back tomorrow — your work is exactly where you left it. Export the source as a `.logoman.json` file to back it up or move it to another machine."
          ctaLabel="Open editor"
          onCta={() => go("editor")}
          art={<HowArt step={4} />}
          flip={true}
        />

        <Step
          num="05"
          title="Download your files"
          body="One-click Brand Kit ZIP gives you multi-resolution PNGs (128 / 256 / 512 / 1024 / 2048), a complete favicon set (16 / 32 / 48 / 96 / 192), the vector SVG, and the source file you can re-import later. Or grab just a single PNG or SVG when that's all you need."
          ctaLabel="Open editor"
          onCta={() => go("editor")}
          art={<HowArt step={5} />}
          flip={false}
        />
      </section>

      {/* ============ WHAT YOU GET ============ */}
      <section className="section-wrap dark">
        <div className="section-head-center light">
          <h2>What's in the Brand Kit ZIP</h2>
          <p>Everything you need to ship — no upsells, no missing files.</p>
        </div>

        <div className="feature-grid">
          <Feature
            icon="🖼"
            title="Multi-size PNGs"
            body="128, 256, 512, 1024, and 2048 px — covers every web, social, and print use case from app icon to billboard."
          />
          <Feature
            icon="🌐"
            title="Full favicon set"
            body="16, 32, 48, 96, and 192 px PNGs ready to drop into the head of any website."
          />
          <Feature
            icon="📐"
            title="Vector SVG"
            body="Scales infinitely without quality loss. Open in Illustrator, Figma, Sketch — or hand it off to a sign printer."
          />
          <Feature
            icon="💾"
            title="Source file"
            body="A .logoman.json you can re-import to edit later. No lock-in, no subscription required to keep editing."
          />
        </div>
      </section>

      {/* ============ COMING SOON ============ */}
      <section className="section-wrap">
        <div className="section-head-center">
          <h2>Coming soon</h2>
          <p>The roadmap — vote with your feature requests.</p>
        </div>

        <div className="feature-grid">
          <Feature icon="📱" title="Mockup gallery" body="Preview your logo on business cards, t-shirts, signage, websites, and app screens — updated live as you edit." badge="planned" />
          <Feature icon="✨" title="AI logo ideas" body="Describe your brand in a sentence — Claude picks the icon, palette, and font pairing for you. Then refine in the editor." badge="planned" />
          <Feature icon="🔗" title="Share link" body="Send a link to a teammate; they see your exact logo state and can fork their own version." badge="planned" />
          <Feature icon="↶" title="Undo / redo" body="Full edit history with keyboard shortcuts, even across sessions." badge="planned" />
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="cta-band">
        <h2>Ready? Make your logo now.</h2>
        <p>Free to design. Free to download. Your draft saves automatically.</p>
        <div className="actions center">
          <button className="btn primary big" onClick={() => go("onboarding")}>Start designing &rarr;</button>
        </div>
      </section>

      <footer className="footer">
        <span className="brand">
          <span className="brand-mark">L</span>
          <span className="brand-name"><span className="brand-www">www.</span>logoman<span className="brand-pro">.pro</span></span>
        </span>
        <span className="footer-meta">© 2026 · Built for the web · 100% client-side</span>
      </footer>
    </div>
  );
}

// ============================================================
// Manifesto poster — cinematic comic-book hero panel
// ============================================================
function ManifestoSection() {
  const setPage = useLogoStore((s) => s.setPage);
  const skills = ["COLOR", "BALANCE", "CONTRAST", "IMPACT"];
  const palette = ["#dc2626", "#1e3a8a", "#3b82f6", "#0c1437", "#94a3b8", "#ffffff"];
  const tools = [
    { glyph: "✒", label: "Pen" },
    { glyph: "A",  label: "Type" },
    { glyph: "◎",  label: "Target" },
    { glyph: "▦",  label: "Grid" },
    { glyph: "⚡",  label: "Impact" },
  ];

  return (
    <section className="manifesto" aria-label="LOGOMAN manifesto">
      <div className="manifesto-bg" />
      <div className="manifesto-inner">
        <ul className="manifesto-skills">
          {skills.map((s) => (
            <li key={s}><span className="skill-bullet" />{s}</li>
          ))}
        </ul>

        <div className="manifesto-quote">
          YOUR BRAND.<br />
          YOUR STORY.<br />
          YOUR LEGACY.<br />
          <span className="accent">I DESIGN VICTORY.</span>
        </div>

        <div className="manifesto-palette">
          {palette.map((c) => <span key={c} style={{ background: c }} />)}
        </div>

        <div className="manifesto-toolbar">
          {tools.map((t) => (
            <span key={t.label} className="tool" title={t.label}>{t.glyph}</span>
          ))}
        </div>

        <div className="manifesto-footer">
          <span>CREATIVE BY NATURE. <span className="accent">HERO BY DESIGN.</span></span>
          <button className="btn primary" onClick={() => setPage("onboarding")}>Start designing &rarr;</button>
        </div>
      </div>
    </section>
  );
}

// ============================================================
// Top nav
// ============================================================
function TopNav({ onSignIn }: { onSignIn: () => void }) {
  const setPage = useLogoStore((s) => s.setPage);
  const savedCount = useLogoStore((s) => s.savedLogos.length);
  const { user, loading } = useAuth();
  return (
    <nav className="nav nav-warm">
      <button className="brand brand-clickable" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
        <span className="brand-mark">L</span>
        <span className="brand-name">Logoman<span className="brand-pro">.pro</span></span>
      </button>
      <div className="nav-actions">
        {savedCount > 0 && (
          <button
            className="nav-link"
            onClick={() => setPage("templates")}
            title="Open your saved designs"
          >
            My Logos ({savedCount})
          </button>
        )}
        {!loading && (user ? (
          <button
            className="nav-account"
            onClick={onSignIn}
            title={user.email || "Account"}
          >
            {user.photoURL ? (
              <img src={user.photoURL} alt="" />
            ) : (
              <span className="nav-account-initial" aria-hidden="true">
                {(user.displayName || user.email || "?").charAt(0).toUpperCase()}
              </span>
            )}
            <span>{user.displayName?.split(" ")[0] || user.email?.split("@")[0] || "Account"}</span>
          </button>
        ) : (
          <button className="btn-warm-outline" onClick={onSignIn}>Log in</button>
        ))}
        <button className="btn-warm-primary" onClick={() => setPage("onboarding")}>
          Get Started Free
        </button>
      </div>
    </nav>
  );
}

// ============================================================
// Confetti decoration — light scattered SVG accents around the hero
// ============================================================
function ConfettiDecor() {
  return (
    <div className="confetti" aria-hidden="true">
      <span className="cf cf-squiggle cf-1" />
      <span className="cf cf-dot cf-2" />
      <span className="cf cf-flower cf-3" />
      <span className="cf cf-squiggle cf-4" />
      <span className="cf cf-dot cf-5" />
      <span className="cf cf-flower cf-6" />
      <span className="cf cf-dot cf-7" />
      <span className="cf cf-squiggle cf-8" />
      <span className="cf cf-flower cf-9" />
    </div>
  );
}

// ============================================================
// Feature grid — "Everything you need to grow your brand"
// ============================================================
function FeatureGrid({ go }: { go: (target: "templates" | "editor" | "onboarding" | "generated" | "gif-maker" | "qr-maker") => void }) {
  return (
    <section className="feature-grid-2">
      <div className="fg2-head">
        <h2>
          Everything you need to <span className="ink-coral">grow</span> your brand
        </h2>
        <p>Powerful tools. Easy to use. Loved by thousands.</p>
      </div>
      <div className="fg2-grid">
        <FGCard
          tone="pink"
          icon={<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 19l7-7 3 3-7 7-3-3z"/><path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/><path d="M2 2l7.586 7.586"/><circle cx="11" cy="11" r="2"/></svg>}
          title="Logo Maker"
          body="Create professional logos in minutes with our easy drag & drop editor."
          ctaLabel="Create Logo"
          onCta={() => go("onboarding")}
        />
        <FGCard
          tone="green"
          icon={<span className="fg2-text-icon">GIF</span>}
          title="Animated GIFs"
          body="Bring your brand to life with custom animated GIFs."
          ctaLabel="Make GIF"
          onCta={() => go("gif-maker")}
        />
        <FGCard
          tone="orange"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1.5"/>
              <rect x="14" y="3" width="7" height="7" rx="1.5"/>
              <rect x="3" y="14" width="7" height="7" rx="1.5"/>
              <path d="M14 14h3v3M21 14v3M14 17v4M17 21h4"/>
            </svg>
          }
          title="QR Code Design"
          body="Design beautiful, branded QR Codes that stand out and get scanned."
          ctaLabel="Design QR Code"
          onCta={() => go("qr-maker")}
        />
        <FGCard
          tone="purple"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
              <path d="M12 12v6"/>
              <path d="M9 15l3-3 3 3"/>
            </svg>
          }
          title="Brand Assets"
          body="Download high-quality assets for all your branding needs."
          ctaLabel="Explore Assets"
          onCta={() => go("templates")}
        />
        <FGCard
          tone="yellow"
          icon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="18" cy="5" r="3"/>
              <circle cx="6" cy="12" r="3"/>
              <circle cx="18" cy="19" r="3"/>
              <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
              <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
            </svg>
          }
          title="Share & Export"
          body="Share your designs easily and export in all popular formats."
          ctaLabel="Learn More"
          onCta={() => go("templates")}
        />
      </div>
    </section>
  );
}

function FGCard({ tone, icon, title, body, ctaLabel, onCta }: {
  tone: "pink" | "green" | "orange" | "purple" | "yellow";
  icon: React.ReactNode;
  title: string;
  body: string;
  ctaLabel: string;
  onCta: () => void;
}) {
  return (
    <div className={`fg2-card fg2-${tone}`}>
      <div className="fg2-icon">{icon}</div>
      <h3>{title}</h3>
      <p>{body}</p>
      <button className="fg2-cta" onClick={onCta}>{ctaLabel}</button>
    </div>
  );
}

// ============================================================
// One numbered "How it works" row, alternating left/right
// ============================================================
function Step({ num, title, body, ctaLabel, onCta, art, flip }: {
  num: string; title: string; body: string; ctaLabel: string; onCta: () => void; art: React.ReactNode; flip: boolean;
}) {
  return (
    <div className={`step-row ${flip ? "flip" : ""}`}>
      <div className="step-copy">
        <span className="step-num">{num}</span>
        <h3>{title}</h3>
        <p>{body}</p>
        <button className="btn primary" onClick={onCta}>{ctaLabel} &rarr;</button>
      </div>
      <div className="step-art">{art}</div>
    </div>
  );
}

// ============================================================
// Feature card (used in both Brand Kit + Coming Soon grids)
// ============================================================
function Feature({ icon, title, body, badge }: { icon: string; title: string; body: string; badge?: string }) {
  return (
    <div className="feature-card">
      <div className="feature-icon">{icon}</div>
      <h4>{title}{badge && <span className="feature-badge">{badge}</span>}</h4>
      <p>{body}</p>
    </div>
  );
}

// ============================================================
// Visual art for each "How it works" step
// All built from real renderLogoSVG output so they look on-brand.
// ============================================================
const SAMPLE_BASE = {
  title: "LOGOMAN PRO", slogan: "design studio", iconId: "spark",
  layout: "icon-top" as const, titleFont: "Montserrat", sloganFont: "Inter",
  titleSize: 56, sloganSize: 18, iconScale: 1,
};

function SamplePreview({ seed, className = "" }: { seed: number; className?: string }) {
  const palettes = [
    { titleColor: "#0c2572", sloganColor: "#475569", iconColor: "#3b82f6", background: "#ffffff" },
    { titleColor: "#facc15", sloganColor: "#fafafa", iconColor: "#facc15", background: "#0f172a" },
    { titleColor: "#0c4a6e", sloganColor: "#0284c7", iconColor: "#0ea5e9", background: "#f0f9ff" },
  ];
  const p = palettes[seed % palettes.length];
  const svg = renderLogoSVG({ ...SAMPLE_BASE, ...p }, { width: 360, height: 360 });
  return <div className={`sample-card ${className}`} dangerouslySetInnerHTML={{ __html: svg }} />;
}

// ============================================================
// Recent logos carousel — shows the user's most recent saves,
// auto-rotating. Falls back to the original 3 sample cards when
// nothing has been saved yet, so the marketing page still looks
// alive for first-time visitors.
// ============================================================
function RecentLogosCarousel() {
  const savedLogos = useLogoStore((s) => s.savedLogos);
  const myTemplates = useLogoStore((s) => s.myTemplates);
  const customIcons = useLogoStore((s) => s.customIcons);
  const loadFromLibrary = useLogoStore((s) => s.loadFromLibrary);
  const loadMyTemplate = useLogoStore((s) => s.loadMyTemplate);
  const setPage = useLogoStore((s) => s.setPage);

  // Community gallery: latest public logos from Firestore, available to every
  // visitor regardless of sign-in. Empty array on any failure (Firestore not
  // enabled, rules deny, offline) — falls through to the user's local saves.
  const [publicLogos, setPublicLogos] = useState<PublicLogoRow[]>([]);
  useEffect(() => {
    let cancelled = false;
    fetchRecentPublicLogos(24).then((rows) => {
      if (!cancelled) setPublicLogos(rows);
    });
    return () => { cancelled = true; };
  }, []);

  // Build the slide list from three sources, deduped.
  // Public Firestore docs come first (community gallery); then the user's own
  // local saves so they still see their work-in-progress alongside the community.
  // Skip any local template that has a firestoreDocId already present in
  // publicLogos — otherwise the same design appears twice for its author.
  type SlideSource = "public" | "library" | "template";
  const publishedDocIds = new Set(publicLogos.map((r) => r.docId));
  const sourceMap = new Map<string, { logo: typeof savedLogos[number]; source: SlideSource }>();
  for (const row of publicLogos) {
    sourceMap.set(`fs:${row.docId}`, { logo: { ...row, id: `fs:${row.docId}` }, source: "public" });
  }
  for (const logo of savedLogos) {
    if (!sourceMap.has(logo.id)) sourceMap.set(logo.id, { logo, source: "library" });
  }
  for (const logo of myTemplates) {
    // Skip local copies of templates the user already published publicly —
    // the Firestore version is canonical and already in the map.
    if (logo.firestoreDocId && publishedDocIds.has(logo.firestoreDocId)) continue;
    if (!sourceMap.has(logo.id)) sourceMap.set(logo.id, { logo, source: "template" });
  }
  const all = [...sourceMap.values()]
    .filter(({ logo }) => logo.iconId !== "__qr__")
    .sort((a, b) => (b.logo.savedAt || "").localeCompare(a.logo.savedAt || ""));

  const slides = all.map(({ logo, source }) => ({
    id: logo.id,
    name: logo.name,
    source,
    svg: renderLogoSVG(
      { ...logo, iconId: logo.iconId ?? null },
      // Landscape canvas (16:10) so horizontal frames (pill, rectangle,
      // oval) fill the card naturally without huge vertical empty bands.
      { width: 480, height: 300, customIcons },
    ),
  }));

  function open(slide: { id: string; source: SlideSource }) {
    if (slide.source === "library") {
      loadFromLibrary(slide.id);
    } else if (slide.source === "template") {
      loadMyTemplate(slide.id);
    } else {
      // Public Firestore logo: hydrate the editor from the row data and treat
      // it as a fresh starting point (no existing local id to load).
      const row = publicLogos.find((r) => `fs:${r.docId}` === slide.id);
      if (row) {
        useLogoStore.getState().pushHistory();
        useLogoStore.setState({
          title: row.title, slogan: row.slogan, iconId: row.iconId,
          titleFont: row.titleFont, sloganFont: row.sloganFont,
          titleColor: row.titleColor, sloganColor: row.sloganColor,
          iconColor: row.iconColor, iconAccent: row.iconAccent,
          background: row.background, layout: row.layout,
          titleSize: row.titleSize, sloganSize: row.sloganSize, iconScale: row.iconScale,
          logoFrame: row.logoFrame, logoFrameColor: row.logoFrameColor,
          logoFrameWidth: row.logoFrameWidth, logoFrameHeight: row.logoFrameHeight,
          logoFrameBorderColor: row.logoFrameBorderColor,
          logoFrameBorderWidth: row.logoFrameBorderWidth,
          logoFrameBorderStyle: row.logoFrameBorderStyle,
          containerShape: row.containerShape, containerColor: row.containerColor,
          containerColorOpacity: row.containerColorOpacity,
          containerGradient: row.containerGradient, containerColor2: row.containerColor2,
          containerGradientAngle: row.containerGradientAngle,
          containerPattern: row.containerPattern, containerPatternColor: row.containerPatternColor,
          containerPatternDensity: row.containerPatternDensity,
          iconPattern: row.iconPattern, iconPatternColor: row.iconPatternColor,
          iconPatternDensity: row.iconPatternDensity,
          titleStyle3D: row.titleStyle3D, titleDepth3D: row.titleDepth3D,
          titleStroke3D: row.titleStroke3D, titleSkew: row.titleSkew,
          sloganStyle3D: row.sloganStyle3D, sloganDepth3D: row.sloganDepth3D,
          sloganStroke3D: row.sloganStroke3D, sloganSkew: row.sloganSkew,
          titleSloganGap: row.titleSloganGap, sloganOffsetX: row.sloganOffsetX,
          iconTextGap: row.iconTextGap, titleOffsetX: row.titleOffsetX,
          titleOffsetY: row.titleOffsetY,
        });
      }
    }
    setPage("editor");
    window.scrollTo({ top: 0 });
  }

  // Zero saves → marketing fallback (3 scattered sample cards) so the hero
  // doesn't render an empty stage on a fresh account.
  if (slides.length === 0) {
    return (
      <>
        <SamplePreview seed={1} />
        <SamplePreview seed={2} className="float-back" />
        <SamplePreview seed={3} className="float-front" />
      </>
    );
  }

  // Polaroid scatter — show up to 8 most-recent saves as tilted photos
  // pinned to a memory board. Hover any to bring it forward.
  // Hand-tuned positions + tilts so the layout feels intentional, not random.
  const POLAROID_LAYOUT = [
    { left: "4%",  top: "2%",  rot: -8 },
    { left: "38%", top: "6%",  rot:  5 },
    { left: "68%", top: "0%",  rot: -4 },
    { left: "12%", top: "30%", rot:  6 },
    { left: "46%", top: "34%", rot: -7 },
    { left: "72%", top: "30%", rot:  9 },
    { left: "6%",  top: "62%", rot:  4 },
    { left: "42%", top: "66%", rot: -6 },
  ];
  const visible = slides.slice(0, POLAROID_LAYOUT.length);

  return (
    <div className="polaroid-board" aria-label="Your saved logos">
      <span className="recent-label">Your saved logos</span>
      <div className="polaroid-scatter">
        {visible.map((slide, i) => {
          const pos = POLAROID_LAYOUT[i];
          return (
            <button
              key={slide.id}
              type="button"
              className="polaroid"
              onClick={() => open(slide)}
              title={`Open "${slide.name}" in the editor`}
              style={{
                left: pos.left,
                top: pos.top,
                ["--rot" as string]: `${pos.rot}deg`,
                zIndex: i + 1,
              }}
            >
              <div className="polaroid-photo" dangerouslySetInnerHTML={{ __html: slide.svg }} />
              <span className="polaroid-caption">{slide.name}</span>
            </button>
          );
        })}
      </div>
      {slides.length > POLAROID_LAYOUT.length && (
        <span className="polaroid-more">
          +{slides.length - POLAROID_LAYOUT.length} more in your library
        </span>
      )}
    </div>
  );
}

function HowArt({ step }: { step: number }) {
  if (step === 1) {
    // Step 1: image + extracted palette swatches
    const colors = ["#1e40af", "#06b6d4", "#facc15", "#f97316", "#0f172a", "#f8fafc"];
    return (
      <div className="how-art how-art-1">
        <div className="how-mock-image">photo · screenshot · swatch</div>
        <div className="how-mock-arrow">↓ extract</div>
        <div className="how-mock-palette">
          {colors.map((c) => <span key={c} style={{ background: c }} />)}
        </div>
      </div>
    );
  }
  if (step === 2) {
    // Step 2: a small grid of templates colored from a palette.
    // Text sizes and pad are tuned to the 200x200 tile so "LOGOMAN PRO"
    // fits at natural letter width — no horizontal glyph squashing.
    const variants = [
      { titleColor: "#1e40af", sloganColor: "#475569", iconColor: "#06b6d4", background: "#f0f9ff" },
      { titleColor: "#0f172a", sloganColor: "#475569", iconColor: "#facc15", background: "#fef9c3" },
      { titleColor: "#fafafa", sloganColor: "#cbd5e1", iconColor: "#06b6d4", background: "#0f172a" },
      { titleColor: "#7c2d12", sloganColor: "#c2410c", iconColor: "#f97316", background: "#fff7ed" },
    ];
    return (
      <div className="how-art how-grid-2">
        {variants.map((v, i) => {
          const svg = renderLogoSVG(
            {
              ...SAMPLE_BASE,
              ...v,
              iconId: ["spark", "diamond", "hex", "drop"][i],
              titleSize: 22,
              sloganSize: 11,
            },
            { width: 200, height: 200, pad: 14 },
          );
          return <div key={i} className="sample-tile" dangerouslySetInnerHTML={{ __html: svg }} />;
        })}
      </div>
    );
  }
  if (step === 3) {
    // Step 3: editor mock — left toolbar + canvas
    const svg = renderLogoSVG({ ...SAMPLE_BASE, titleColor: "#0c2572", sloganColor: "#475569", iconColor: "#3b82f6", background: "#ffffff" }, { width: 320, height: 320 });
    return (
      <div className="how-art how-editor">
        <div className="how-editor-panel">
          <div className="he-tabs"><span className="he-tab active">Icon</span><span className="he-tab">Text</span><span className="he-tab">Color</span></div>
          <div className="he-grid">{Array.from({ length: 8 }).map((_, i) => <span key={i} className="he-cell" />)}</div>
          <div className="he-row">
            <span className="he-swatch" style={{ background: "#0c2572" }} />
            <span className="he-swatch" style={{ background: "#3b82f6" }} />
            <span className="he-swatch" style={{ background: "#facc15" }} />
            <span className="he-swatch" style={{ background: "#16a34a" }} />
          </div>
        </div>
        <div className="how-editor-canvas" dangerouslySetInnerHTML={{ __html: svg }} />
      </div>
    );
  }
  if (step === 4) {
    return (
      <div className="how-art how-save">
        <div className="how-save-card">
          <div className="how-dot" />
          <div>
            <strong>Auto-saved</strong>
            <small>just now · in your browser</small>
          </div>
        </div>
        <div className="how-save-card secondary">
          <div className="how-dot" />
          <div>
            <strong>logoman-pro.logoman.json</strong>
            <small>downloaded · 2.1 KB</small>
          </div>
        </div>
        <div className="how-save-card secondary">
          <div className="how-dot" />
          <div>
            <strong>Resumed editing</strong>
            <small>1 day later · all edits intact</small>
          </div>
        </div>
      </div>
    );
  }
  // Step 5: brand kit ZIP contents
  return (
    <div className="how-art how-zip">
      <div className="how-zip-head">📦 logoman-pro-brand-kit.zip</div>
      {[
        ["png/", "logoman-pro-128.png · 256 · 512 · 1024 · 2048"],
        ["favicon/", "favicon-16 · 32 · 48 · 96 · 192"],
        ["svg/", "logoman-pro.svg · vector source"],
        ["source/", "logoman-pro.logoman.json · re-import to edit"],
        ["", "README.txt"],
      ].map(([p, n], i) => (
        <div key={i} className="how-zip-row">
          <span className="how-zip-folder">{p}</span>
          <span className="how-zip-name">{n}</span>
        </div>
      ))}
    </div>
  );
}
